/**
 * Runtime config — merges `window.SmootifyUserOptions` (Webflow Head Code)
 * with an optional remote `/config.json` served from the same origin as the
 * bundle itself. Permet de garder les secrets (storefrontToken) hors du HTML
 * Webflow : le bundle fetch son propre config.json au boot.
 *
 * Priorité (du plus fort au plus faible) :
 *   1. `window.SmootifyUserOptions.*` (inline Webflow)
 *   2. `config.json` servi à `<bundleUrl>/config.json`
 *   3. Defaults constants.
 *
 * `config` reste un objet gelé exposé de façon synchrone ; `ensureConfigReady`
 * retourne une Promise qui résout une fois le fetch remote terminé.
 */

// ---------------------------------------------------------------------------
//  Public types
// ---------------------------------------------------------------------------

export interface SmootifyUserOptions {
  productsBase?: string;
  collectionsBase?: string;
  accountBase?: string;
  newCustomerAccountsPublicKey?: string;
  invalidProductRedirectUrl?: string;
  newCustomerLoginRedirect?: string;
  orderPageUrl?: string;
  selectMarketBasedOnBrowserLanguage?: boolean;
  enableAutoTrack?: boolean;
  useBrowserFormatter?: boolean;
  preload?: boolean;
  cacheDuration?: number | undefined;
  // LMDR-specific
  shopDomain?: string;
  storefrontToken?: string;
  storefrontApiVersion?: string;
  defaultCountry?: string;
  defaultLanguage?: string;
  // Override si le user ne veut pas du fetch remote automatique.
  remoteConfigUrl?: string | null;
  // Endpoint worker LMDR qui retourne la whitelist des handles publiés sur
  // le channel "Headless" côté Shopify. Utilisé par la page catalogue
  // `<smootify-product data-id="filter">`. Par défaut : dérivé de l'URL du
  // bundle (ex: "https://vps.../sync/headless-handles.json").
  headlessHandlesUrl?: string | null;
  // Endpoint worker LMDR qui crée un draft order Shopify (mode "devis").
  // Utilisé par <smootify-cart data-draft="true">. Par défaut : dérivé
  // de l'URL du bundle (ex: ".../sync/draft-orders/create").
  draftOrdersUrl?: string | null;
}

export interface RuntimeConfig {
  productsBase: string;
  collectionsBase: string;
  accountBase: string;
  newCustomerAccountsPublicKey: string;
  invalidProductRedirectUrl: string;
  newCustomerLoginRedirect: string;
  orderPageUrl: string;
  selectMarketBasedOnBrowserLanguage: boolean;
  enableAutoTrack: boolean;
  useBrowserFormatter: boolean;
  preload: boolean;
  cacheDuration: number;
  shopDomain: string;
  storefrontToken: string;
  storefrontApiVersion: string;
  defaultCountry: string;
  defaultLanguage: string;
  headlessHandlesUrl: string | null;
  draftOrdersUrl: string | null;
  isDev: boolean;
}

declare global {
  interface Window {
    SmootifyUserOptions?: SmootifyUserOptions;
  }
}

// ---------------------------------------------------------------------------
//  Defaults
// ---------------------------------------------------------------------------

const DEFAULTS = {
  productsBase: "product",
  collectionsBase: "collection",
  accountBase: "account",
  newCustomerAccountsPublicKey: "",
  invalidProductRedirectUrl: "/",
  newCustomerLoginRedirect: "",
  orderPageUrl: "/account/order",
  selectMarketBasedOnBrowserLanguage: true,
  enableAutoTrack: false,
  useBrowserFormatter: false,
  preload: true,
  cacheDuration: 60_000,
  storefrontApiVersion: "2026-04",
  defaultCountry: "FR",
  defaultLanguage: "FR",
} as const;

// ---------------------------------------------------------------------------
//  Utilities
// ---------------------------------------------------------------------------

function isDevHost(): boolean {
  if (typeof window === "undefined") return true;
  const h = window.location.hostname;
  return (
    h === "localhost" ||
    h === "127.0.0.1" ||
    h.endsWith(".webflow.io") ||
    h.endsWith(".local")
  );
}

function pickString<T extends string>(raw: unknown, fallback: T): string {
  return typeof raw === "string" && raw.length > 0 ? raw : fallback;
}

function pickBool(raw: unknown, fallback: boolean): boolean {
  return typeof raw === "boolean" ? raw : fallback;
}

function pickNumber(raw: unknown, fallback: number): number {
  return typeof raw === "number" && Number.isFinite(raw) ? raw : fallback;
}

/**
 * Détecte l'URL du bundle courant pour en dériver l'URL du config.json.
 * Utilise `import.meta.url` qui pointe sur le script module.
 * Fallback : scan le DOM pour un <script src="*lmdr-commerce*index.js">.
 */
function resolveRemoteConfigUrl(override: string | null | undefined): string | null {
  if (override === null) return null; // explicite opt-out
  if (typeof override === "string" && override.length > 0) return override;

  try {
    const here = new URL(import.meta.url);
    return new URL("./config.json", here).toString();
  } catch {
    /* import.meta.url not available (edge case); fallthrough */
  }

  if (typeof document === "undefined") return null;
  const scripts = Array.from(
    document.querySelectorAll<HTMLScriptElement>('script[src*="lmdr-commerce"]'),
  );
  for (const s of scripts) {
    if (s.src.endsWith("/index.js")) {
      return s.src.replace(/\/index\.js(\?.*)?$/, "/config.json");
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
//  Build config (mutable internally, frozen after ensureConfigReady)
// ---------------------------------------------------------------------------

function buildConfig(partial: SmootifyUserOptions, dev: boolean): RuntimeConfig {
  const newCustomerLoginRedirect = pickString(
    partial.newCustomerLoginRedirect,
    typeof window !== "undefined" ? window.location.origin : "",
  );
  return {
    productsBase: pickString(partial.productsBase, DEFAULTS.productsBase),
    collectionsBase: pickString(partial.collectionsBase, DEFAULTS.collectionsBase),
    accountBase: pickString(partial.accountBase, DEFAULTS.accountBase),
    newCustomerAccountsPublicKey: pickString(
      partial.newCustomerAccountsPublicKey,
      DEFAULTS.newCustomerAccountsPublicKey,
    ),
    invalidProductRedirectUrl: pickString(
      partial.invalidProductRedirectUrl,
      DEFAULTS.invalidProductRedirectUrl,
    ),
    newCustomerLoginRedirect,
    orderPageUrl: pickString(partial.orderPageUrl, DEFAULTS.orderPageUrl),
    selectMarketBasedOnBrowserLanguage: pickBool(
      partial.selectMarketBasedOnBrowserLanguage,
      DEFAULTS.selectMarketBasedOnBrowserLanguage,
    ),
    enableAutoTrack: pickBool(partial.enableAutoTrack, DEFAULTS.enableAutoTrack),
    useBrowserFormatter: pickBool(
      partial.useBrowserFormatter,
      DEFAULTS.useBrowserFormatter,
    ),
    preload: pickBool(partial.preload, DEFAULTS.preload),
    cacheDuration: pickNumber(partial.cacheDuration, DEFAULTS.cacheDuration),
    shopDomain: pickString(partial.shopDomain, ""),
    storefrontToken: pickString(partial.storefrontToken, ""),
    storefrontApiVersion: pickString(
      partial.storefrontApiVersion,
      DEFAULTS.storefrontApiVersion,
    ),
    defaultCountry: pickString(partial.defaultCountry, DEFAULTS.defaultCountry),
    defaultLanguage: pickString(partial.defaultLanguage, DEFAULTS.defaultLanguage),
    // Si null explicite → pas de fetch headless. Si undefined → dérivé de
    // l'URL du bundle au runtime (voir resolveHeadlessHandlesUrl()).
    headlessHandlesUrl:
      partial.headlessHandlesUrl === null
        ? null
        : partial.headlessHandlesUrl ?? null,
    draftOrdersUrl:
      partial.draftOrdersUrl === null
        ? null
        : partial.draftOrdersUrl ?? null,
    isDev: dev,
  };
}

// ---------------------------------------------------------------------------
//  Public : config (live) + ensureConfigReady (boot gate)
// ---------------------------------------------------------------------------

const dev = isDevHost();
const inline: SmootifyUserOptions =
  (typeof window !== "undefined" && window.SmootifyUserOptions) || {};

// Construction synchrone à partir de l'inline + defaults (pas encore le remote).
export const config: RuntimeConfig = buildConfig(inline, dev);

let readyPromise: Promise<void> | null = null;

/**
 * Assure que la config est complète (fetch du config.json distant si token
 * / domaine manquent dans l'inline). Appelé au boot par `index.ts`.
 * Idempotent.
 */
export function ensureConfigReady(): Promise<void> {
  if (readyPromise) return readyPromise;
  readyPromise = (async () => {
    if (config.shopDomain && config.storefrontToken) return; // already complete
    const remoteUrl = resolveRemoteConfigUrl(inline.remoteConfigUrl);
    if (!remoteUrl) {
      if (dev) {
        // eslint-disable-next-line no-console
        console.warn(
          "[LMDR runtime] No remote config URL resolved — config incomplete (shopDomain / storefrontToken).",
        );
      }
      return;
    }
    try {
      const res = await fetch(remoteUrl, { credentials: "omit" });
      if (!res.ok) throw new Error(`remote config HTTP ${res.status}`);
      const remote = (await res.json()) as Partial<SmootifyUserOptions>;
      // Les valeurs inline gardent priorité sur le remote.
      const merged: SmootifyUserOptions = { ...remote, ...inline };
      Object.assign(config, buildConfig(merged, dev));
    } catch (err) {
      const msg = `[LMDR runtime] Failed to load remote config (${remoteUrl}): ${(err as Error).message}`;
      if (dev) {
        // eslint-disable-next-line no-console
        console.warn(msg);
      } else {
        throw new Error(msg);
      }
    }
    if (!config.shopDomain || !config.storefrontToken) {
      const msg =
        "[LMDR runtime] shopDomain/storefrontToken still missing after remote config load.";
      if (dev) {
        // eslint-disable-next-line no-console
        console.warn(msg);
      } else {
        throw new Error(msg);
      }
    }
  })();
  return readyPromise;
}
